// 钓鱼: where an otter may fish, derived from the map's grids (no spots marked in the map). Pure (no DOM), read by
// the office (src/office/fishing.ts bundles it), by the click-select panel (public/pick.js: the sea's 「钓鱼」) and by
// 钓鱼 mode (public/fishing-mode.js). Kept apart from the fish table and the reel (public/fishing-core.js) so the page
// loads only this small part until someone opens 钓鱼.
// ---- where you may fish
// You stand on dry deck or beach (the tile under your feet: not sea, not a wall), outside every room and game hut, and
// facing the sea within 1–2 tiles (only open deck in between). The bobber lands on the first sea tile that way, or
// the one after it if that's sea too (about 2 tiles out). Deep water: the raft's edges (the biggest piece of land);
// shallow: any other piece of land (the islands' beaches). So every edge of the map is a spot, with nothing marked.
export const DIRS = [[0, 1], [1, 0], [0, -1], [-1, 0]]; // dir 0 down, 1 right, 2 up, 3 left (as the otters face)
const SEA_MARGIN = 14; // open sea around the map (rules.ts / collide.js)
// (a grown map's grids start at x0, y0: deck/map-overlay.js applyOverlay)
const X0 = (M) => M.x0 ?? 0, Y0 = (M) => M.y0 ?? 0;
const inMap = (M, x, y) => x >= X0(M) && y >= Y0(M) && x < X0(M) + M.width && y < Y0(M) + M.height;
const seaAt = (M, x, y) => (inMap(M, x, y) ? M.water[y - Y0(M)][x - X0(M)] === '1' : x >= X0(M) - SEA_MARGIN && y >= Y0(M) - SEA_MARGIN && x < X0(M) + M.width + SEA_MARGIN && y < Y0(M) + M.height + SEA_MARGIN);
const deckAt = (M, x, y) => inMap(M, x, y) && M.water[y - Y0(M)][x - X0(M)] !== '1' && M.solid[y - Y0(M)][x - X0(M)] !== '1';
const inZone = (M, fx, fy) => (M.zones || []).some((z) => fx >= z.x && fx < z.x + z.w && fy >= z.y && fy < z.y + z.h);

const islandsCache = new WeakMap();
/** Each non-sea tile's piece of land (4-connected), and which piece is the raft (the biggest). */
function islands(M) {
  let c = islandsCache.get(M);
  if (c && c.water === M.water) return c; // (the page's MAP changes in place on a map edit: its rows are new then)
  const id = new Int32Array(M.width * M.height).fill(-1), sizes = [];
  for (let y = 0; y < M.height; y++) for (let x = 0; x < M.width; x++) {
    if (id[y * M.width + x] >= 0 || M.water[y][x] === '1') continue;
    const n = sizes.length, q = [[x, y]]; id[y * M.width + x] = n; let size = 0;
    while (q.length) {
      const [cx, cy] = q.pop(); size++;
      for (const [dx, dy] of DIRS) {
        const nx = cx + dx, ny = cy + dy;
        if (nx >= 0 && ny >= 0 && nx < M.width && ny < M.height && id[ny * M.width + nx] < 0 && M.water[ny][nx] !== '1') { id[ny * M.width + nx] = n; q.push([nx, ny]); } // (grid cells, not world tiles)
      }
    }
    sizes.push(size);
  }
  c = { id, raft: sizes.indexOf(Math.max(...sizes)) };
  c.water = M.water; islandsCache.set(M, c);
  return c;
}

/** From a standing tile, facing dir: the bobber's tile, or null. */
function castFrom(M, fx, fy, dir) {
  const [dx, dy] = DIRS[dir];
  for (let d = 1; d <= 2; d++) {
    const x = fx + dx * d, y = fy + dy * d;
    if (seaAt(M, x, y)) return seaAt(M, x + dx, y + dy) ? [x + dx, y + dy] : [x, y];
    if (!deckAt(M, x, y)) return null; // a wall in the way
  }
  return null;
}

/**
 * Can an otter at position (x, y) (the sprite's top-left, in tiles, as on the wire) cast? Facing `prefer` first, then
 * any other way that faces the sea (so a press of F at the edge just works). Returns {dir, bx, by, water} or null.
 */
export function findSpot(M, x, y, prefer = 0) {
  const fx = Math.floor(x + 0.5), fy = Math.floor(y + 0.8); // the feet (rules.ts feet)
  if (!deckAt(M, fx, fy) || inZone(M, x + 0.5, y + 0.8)) return null;
  for (const dir of [prefer & 3, 0, 1, 2, 3]) {
    const b = castFrom(M, fx, fy, dir);
    if (!b) continue;
    const isl = islands(M);
    return { dir, bx: b[0], by: b[1], water: isl.id[(fy - Y0(M)) * M.width + fx - X0(M)] === isl.raft ? 'deep' : 'shallow' };
  }
  return null;
}

